// src/components/website-preview/WebsiteRenderer.js
// Pure rendering component for practitioner websites.
// Accepts ALL website data as props — no Context, no localStorage, no editing features.
// Used by:
//   WebsitePreview.js  — passes draft data from WebsiteBuilderContext (for Site Editor)
//   PublicWebsite.js   — passes published data from Firestore (for public visitors)

import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Pencil } from 'lucide-react';
import SocialIcons from '../SocialIcons';
import BadgeDisplay from '../shared/BadgeDisplay';
import { sanitizeHtml } from '../../utils/sanitize';
import FAQAccordionItem from './FAQAccordionItem';
import TestimonialCard from './TestimonialCard';
import GalleryGrid from './GalleryGrid';
import GalleryMasonry from './GalleryMasonry';
import Lightbox from './Lightbox';
import NewsletterSignup from './NewsletterSignup';
import ServiceDetailPage from './ServiceDetailPage';
import PoliciesPage from './PoliciesPage';
import EventsSection from '../website/EventsSection';
import BookingPage from './BookingPage';
import {
  getTextColor,
  getSecondaryTextColor,
  hexToRgba,
  getAltBackground,
  getFooterBackground,
} from '../../utils/colorUtils';

// ─── Layout style definitions ────────────────────────────────────────────────

const LAYOUT_STYLES = {
  minimal: {
    name: 'Minimal',
    description: 'Clean lines, subtle borders, sophisticated restraint',
    borderRadius: '0px',
    cardRadius: '0px',
    buttonRadius: '0px',
    shadow: 'none',
    cardShadow: 'none',
    cardBorder: true,
    navStyle: 'light',
    buttonStyle: 'outlined',
  },
  warm: {
    name: 'Warm',
    description: 'Soft curves, gentle shadows, inviting feel',
    borderRadius: '24px',
    cardRadius: '20px',
    buttonRadius: '9999px',
    shadow: '0 8px 30px -8px rgba(0,0,0,0.12)',
    cardShadow: '0 4px 20px -4px rgba(0,0,0,0.08)',
    cardBorder: false,
    navStyle: 'colored',
    buttonStyle: 'filled',
  },
  bold: {
    name: 'Bold',
    description: 'Strong presence, dramatic shadows, confident impact',
    borderRadius: '12px',
    cardRadius: '12px',
    buttonRadius: '8px',
    shadow: '0 16px 48px -12px rgba(0,0,0,0.25)',
    cardShadow: '0 8px 30px -8px rgba(0,0,0,0.15)',
    cardBorder: false,
    navStyle: 'colored',
    buttonStyle: 'chunky',
  },
};

// ─── Name style options ───────────────────────────────────────────────────────

const NAME_STYLES = [
  {
    id: 'elegant',
    style: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: '500',
      fontStyle: 'italic',
      letterSpacing: '0.02em',
    },
  },
  {
    id: 'modern',
    style: {
      fontFamily: "'Montserrat', 'Helvetica Neue', sans-serif",
      fontWeight: '300',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
    },
  },
  {
    id: 'bold',
    style: {
      fontFamily: "'Poppins', 'Arial Black', sans-serif",
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },
  {
    id: 'handwritten',
    style: {
      fontFamily: "'Dancing Script', 'Brush Script MT', cursive",
      fontWeight: '500',
      letterSpacing: '0.01em',
    },
  },
];

// ─── Font pairing options ─────────────────────────────────────────────────────

const FONT_STYLES = [
  // Current 6 options
  { id: 'playfair',   headingFont: "'Playfair Display', Georgia, serif",     bodyFont: "'Lato', Arial, sans-serif" },
  { id: 'inter',      headingFont: "'Inter', Arial, sans-serif",              bodyFont: "'Inter', Arial, sans-serif" },
  { id: 'lora',       headingFont: "'Lora', Georgia, serif",                  bodyFont: "'Lato', Arial, sans-serif" },
  { id: 'outfit',     headingFont: "'Outfit', Arial, sans-serif",             bodyFont: "'Outfit', Arial, sans-serif" },
  { id: 'raleway',    headingFont: "'Raleway', Arial, sans-serif",            bodyFont: "'Lato', Arial, sans-serif" },
  { id: 'montserrat', headingFont: "'Montserrat', Arial, sans-serif",         bodyFont: "'Open Sans', Arial, sans-serif" },
  // Legacy IDs — keep for backward compatibility with existing saved sites
  { id: 'classic', headingFont: "'Playfair Display', Georgia, serif",     bodyFont: "'Lato', Arial, sans-serif" },
  { id: 'modern',  headingFont: "'Montserrat', Arial, sans-serif",        bodyFont: "'Open Sans', Arial, sans-serif" },
  { id: 'warm',    headingFont: "'Cormorant Garamond', Georgia, serif",   bodyFont: "'Nunito', Arial, sans-serif" },
  { id: 'bold',    headingFont: "'Poppins', Arial, sans-serif",           bodyFont: "'Inter', Arial, sans-serif" },
];

// ─── Preset colors ────────────────────────────────────────────────────────────

const PRESET_COLORS = {
  'sage-cream': { primary: '#5C7F67', secondary: '#D4A574', background: '#FAF8F5' },
  'lavender-gold': { primary: '#7C3AED', secondary: '#D4AF37', background: '#FAF5FF' },
  'ocean-sand': { primary: '#0369A1', secondary: '#D4A574', background: '#F0F9FF' },
  'rose-blush': { primary: '#BE185D', secondary: '#F9A8D4', background: '#FFF1F2' },
  'midnight-gold': { primary: '#6366F1', secondary: '#D4AF37', background: '#0F172A' },
};

// ─── Modality display names ───────────────────────────────────────────────────

const MODALITY_NAMES = {
  'divination': 'Divination',
  'astrology': 'Astrology',
  'numerology': 'Numerology',
  'mediumship': 'Mediumship & Channeling',
  'energy-healing': 'Energy Healing',
  'spiritual-coaching': 'Spiritual Coaching',
  'breathwork-meditation': 'Breathwork & Meditation',
  'sound-healing': 'Sound Healing',
  'hypnotherapy': 'Hypnotherapy & Trance Work',
  'other': 'Other',
};

// ─── ServiceCard component ────────────────────────────────────────────────────

const ServiceCard = ({ service, primaryColor, textColor, headingFont, bodyFont, onClick }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const getSessionTypeDisplay = () => {
    if (service.serviceType === 'in-person') return 'In-Person';
    if (service.serviceType === 'phone') return 'Phone';
    if (service.serviceType === 'hybrid') return 'Hybrid';
    return 'Virtual';
  };

  return (
    <div
      style={{
        border: `1px solid ${isHovered ? primaryColor : '#E5E7EB'}`,
        borderRadius: '12px',
        padding: '24px',
        background: '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={(e) => { e.stopPropagation(); setIsHovered(true); }}
      onMouseLeave={(e) => { e.stopPropagation(); setIsHovered(false); }}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(service.id); }}
    >
      {service.serviceImage && (
        <div style={{ width: '100%', height: '140px', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
          <img
            src={service.serviceImage}
            alt={service.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <div style={{ flex: 1 }}>
        <h3 style={{ fontWeight: 600, fontSize: '17px', color: textColor, marginBottom: '6px', fontFamily: headingFont }}>
          {service.name}
        </h3>
        {service.tagline && (
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5', marginBottom: '12px', fontFamily: bodyFont }}>
            {service.tagline}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#9CA3AF', marginBottom: '12px', fontFamily: bodyFont }}>
          <span>{service.duration} min</span>
          <span>·</span>
          <span style={{ textTransform: 'capitalize' }}>{getSessionTypeDisplay()}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
        <span style={{ fontWeight: 700, fontSize: '20px', color: textColor, fontFamily: headingFont }}>
          {service.price === 0 ? 'Free' : `$${service.price}`}
        </span>
        <span style={{ fontSize: '13px', fontWeight: 500, color: primaryColor, display: 'flex', alignItems: 'center', gap: '4px', fontFamily: bodyFont }}>
          Learn More
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </div>
  );
};

// ─── Main WebsiteRenderer component ──────────────────────────────────────────

const WebsiteRenderer = ({
  design,
  hero,
  content,
  contactFooter,
  marketing,
  profile,
  sectionOrder,
  sectionVisibility,
  practitionerId,
  practitionerName,
  verification = null,
  availability = {},
  publishedReviews = [],
  googleReviews = [],
  embedded = false,
  onSectionClick = null,
  activeSection = null,
  activePanel = null,
  isPublicSite = false,
  practitionerSlug = '',
  policies = null,
  canonicalProfilePhoto = null,
}) => {
  // Reconstruct draft-compatible object (minimizes JSX changes vs. original)
  const draft = {
    design: design || {},
    hero: hero || {},
    content: content || {},
    contactFooter: contactFooter || {},
    marketing: marketing || {},
    profile: profile || {},
    sectionOrder: sectionOrder || [],
    sectionVisibility: sectionVisibility || {},
  };

  // Read-with-fallback for policies during Phase 2 IA migration window.
  // Canonical home is practitioner.policies.{cancellation|privacy|terms};
  // legacy is contactFooter.{cancellationPolicy|privacyPolicy|termsOfService}.
  // See docs/DF-IA-Restructure-Spec-May2026.md Decision 2.
  const effectiveCancellationPolicy = policies?.cancellation || draft.contactFooter?.cancellationPolicy || null;
  const effectivePrivacyPolicy = policies?.privacy || draft.contactFooter?.privacyPolicy || null;
  const effectiveTermsOfService = policies?.terms || draft.contactFooter?.termsOfService || null;

  // ── Internal UI state ────────────────────────────────────────────────────────
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [hoveredSection, setHoveredSection] = useState(null);
  const [viewingServiceDetail, setViewingServiceDetail] = useState(null);
  const [showPoliciesPage, setShowPoliciesPage] = useState(false);
  const [policiesScrollTarget, setPoliciesScrollTarget] = useState(null);
  const [showBookingPage, setShowBookingPage] = useState(false);
  const [bookingService, setBookingService] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ── Scroll animation state ─────────────────────────────────────────────────
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const sectionAnimRefs = React.useRef({});

  // Reset sub-page navigation when the active panel changes in the Site Editor
  useEffect(() => {
    if (embedded && activePanel !== null) {
      setViewingServiceDetail(null);
      setShowPoliciesPage(false);
      setPoliciesScrollTarget(null);
      setShowBookingPage(false);
      setBookingService(null);
      setLightboxOpen(false);
    }
  }, [activePanel, embedded]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── Scroll animation observer ──────────────────────────────────────────────
  const scrollAnimation = draft.design.scrollAnimation || 'fadeIn';
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const effectiveAnimation = prefersReducedMotion ? 'none' : scrollAnimation;

  const getScrollParent = (el) => {
    if (!el) return null;
    let parent = el.parentElement;
    while (parent) {
      const overflow = window.getComputedStyle(parent).overflow;
      if (overflow === 'auto' || overflow === 'scroll') return parent;
      parent = parent.parentElement;
    }
    return null;
  };

  useEffect(() => {
    if (effectiveAnimation === 'none') return;

    setAnimatedSections(new Set());

    const firstRef = Object.values(sectionAnimRefs.current).find(Boolean);
    const scrollParent = getScrollParent(firstRef);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections((prev) => {
              const next = new Set(prev);
              next.add(entry.target.dataset.section);
              return next;
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 0px 0px', root: scrollParent || null }
    );

    Object.values(sectionAnimRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    // Initial check for sections already visible in the viewport
    const initialTimeout = setTimeout(() => {
      const root = scrollParent || null;
      Object.entries(sectionAnimRefs.current).forEach(([id, el]) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        let containerRect;
        if (root) {
          containerRect = root.getBoundingClientRect();
        } else {
          containerRect = { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
        }
        if (rect.top < containerRect.bottom && rect.bottom > containerRect.top) {
          setAnimatedSections(prev => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
          observer.unobserve(el);
        }
      });
    }, 100);

    // Safety net: force all sections visible after 2s to prevent permanent invisibility
    const safetyTimeout = setTimeout(() => {
      setAnimatedSections(prev => {
        const next = new Set(prev);
        Object.keys(sectionAnimRefs.current).forEach(id => {
          if (sectionAnimRefs.current[id]) next.add(id);
        });
        return next;
      });
    }, 2000);

    return () => { observer.disconnect(); clearTimeout(initialTimeout); clearTimeout(safetyTimeout); };
  }, [effectiveAnimation]);

  // Returns animation styles for a given section (no-op if animation is 'none')
  const getScrollAnimStyle = (sectionId) => {
    if (effectiveAnimation === 'none') return {};
    const isVisible = animatedSections.has(sectionId);
    const base = { transition: 'opacity 0.8s cubic-bezier(0.25, 0.1, 0.25, 1), transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)' };
    switch (effectiveAnimation) {
      case 'fadeIn':
        return { ...base, opacity: isVisible ? 1 : 0 };
      case 'slideUp':
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)' };
      case 'scale':
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? 'scale(1)' : 'scale(0.9)' };
      default:
        return {};
    }
  };

  // Ref setter for animated sections
  const setAnimRef = (sectionId) => (el) => { sectionAnimRefs.current[sectionId] = el; };

  // ── Section interaction helpers (embedded/editing mode) ──────────────────────

  const sectionLabels = {
    hero: 'Hero',
    about: 'About',
    services: 'Services',
    testimonials: 'Testimonials',
    faqs: 'FAQs',
    gallery: 'Gallery',
    contact: 'Contact',
    footer: 'Footer',
    newsletter: 'Newsletter',
  };

  const handleSectionClick = (sectionId, panelId) => {
    setViewingServiceDetail(null);
    if (onSectionClick && embedded) {
      onSectionClick(panelId);
    }
  };

  const isSectionActive = (sectionId) => activeSection === sectionId && embedded;

  const getHoverStyle = (sectionId) => {
    if (!embedded) return {};
    const isHovered = hoveredSection === sectionId;
    const isActive = isSectionActive(sectionId);
    return {
      position: 'relative',
      cursor: 'pointer',
      boxShadow: (isActive || isHovered) ? 'inset 0 0 0 2px #7c3aed' : 'none',
      borderRadius: '4px',
      transition: 'box-shadow 0.15s ease',
    };
  };

  const getSectionClassName = (sectionId) => embedded ? 'transition-all' : '';

  const renderSectionLabel = (sectionId) => {
    if (!embedded || hoveredSection !== sectionId) return null;
    const isHero = sectionId === 'hero';
    const labelStyle = {
      position: 'absolute',
      top: '8px',
      left: isHero ? '8px' : '50%',
      transform: isHero ? 'none' : 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      background: '#7c3aed',
      color: '#FFFFFF',
      fontSize: '11px',
      fontWeight: 600,
      padding: '4px 10px',
      borderRadius: '4px',
      zIndex: 10,
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      boxShadow: '0 2px 6px rgba(124, 58, 237, 0.3)',
      animation: 'fadeIn 0.15s ease',
    };
    return (
      <div style={labelStyle}>
        <Pencil size={11} color="white" />
        {sectionLabels[sectionId] || sectionId}
      </div>
    );
  };

  // ── Data extraction from draft ───────────────────────────────────────────────

  const draftFAQs = (draft.content.faqs || []).filter(faq => faq.active);
  const draftTestimonials = (draft.content.testimonials || []).filter(t => t.active);

  let draftGallery = draft.content.gallery || [];
  if (!Array.isArray(draftGallery)) {
    draftGallery = draftGallery.images || [];
  }

  const galleryLayout = draft.content.galleryLayout || draft.content.gallery?.layout || 'grid';
  const showGallery = draft.content.showGallery !== false;
  const draftNewsletter = draft.marketing?.newsletter;

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Read-with-fallback for profile photo during the transition window.
  // Canonical (Settings → Public Profile) saves to practitioner.profile.photo (top-level).
  // Legacy (website-builder draft) reads from draft.profile.photo (nested in website blob).
  // Settings writes don't sync to draft, so existing practitioners may have the photo
  // only at the canonical path. Same shape as the IA Phase 2 policies fix.
  const effectiveProfilePhoto = draft.profile?.photo || canonicalProfilePhoto || null;
  if (!draft.profile?.photo && canonicalProfilePhoto) {
    console.log('[profile-migration] Fallback read for profile photo on practitioner', practitionerId);
  }

  // Profile with social links compatibility
  const profileData = {
    ...draft.profile,
    photo: effectiveProfilePhoto,
    socialLinks: draft.contactFooter.socialLinks,
  };
  const services = draft.content.services || [];

  // About content — handle both string and object schema
  const aboutData = draft.content.about;
  const aboutContent = typeof aboutData === 'string'
    ? aboutData
    : (aboutData?.bio || aboutData?.extendedAbout || '');

  // Display name comes from prop
  const displayName = practitionerName || 'Your Name';

  // Website variable mapping
  const website = {
    layoutStyle: draft.design.layoutStyle,
    template: draft.design.layoutStyle,
    colorScheme: draft.design.colorScheme,
    customColors: draft.design.customColors,
    fontStyle: draft.design.fontStyle,
    buttonStyle: draft.design.buttonStyle,
    logoOption: draft.contactFooter?.logo?.type,
    styledNameFont: draft.contactFooter?.logo?.styledTextFont,
    uploadedLogo: draft.contactFooter?.logo?.uploadedImage,
    socialStyle: {
      style: draft.contactFooter.socialIconStyle,
      shape: draft.contactFooter.socialIconShape || 'circle',
      location: draft.contactFooter.socialDisplayLocation,
    },
    socialVerified: draft.contactFooter?.socialVerified || {},
    heroSettings: draft.hero,
    aboutPhoto: draft.content.aboutPhoto || {
      show: false,
      shape: 'circle',
      size: 'medium',
      position: 'left',
      border: 'none',
    },
  };

  // ── Display modalities ───────────────────────────────────────────────────────
  // Handles both formats:
  //   Legacy: flat array of ID strings  ['divination', 'energy-healing']
  //   New:    structured object          { selected: [{id, name, ...}], custom: [...] }

  const getDisplayModalities = () => {
    const raw = draft.profile.modalities;
    if (!raw) return [];

    // New structured format (ModalitySelector)
    if (typeof raw === 'object' && !Array.isArray(raw) && raw.selected !== undefined) {
      return [
        ...(raw.selected || []).map(m => m.name || '').filter(Boolean),
        ...(raw.custom || []).map(m => m.name || '').filter(Boolean),
      ];
    }

    // Legacy flat array of ID strings
    if (Array.isArray(raw)) {
      return raw
        .filter(id => id !== 'other')
        .map(id => MODALITY_NAMES[id] || id);
    }

    return [];
  };

  const modalities = getDisplayModalities();

  // ── Layout & style computations ──────────────────────────────────────────────

  const layoutStyle = website.layoutStyle || website.template || 'warm';
  const layout = LAYOUT_STYLES[layoutStyle] || LAYOUT_STYLES.warm;

  const aboutPhotoSettings = website.aboutPhoto || {
    show: false, shape: 'circle', size: 'medium', position: 'left', border: 'none',
  };

  // Social links
  const socialLinks = profileData.socialLinks || {};
  const socialStyle = website.socialStyle || {};
  const socialVerified = website.socialVerified || {};
  const socialLocation = socialStyle.location || 'footer';
  const hasConnectedSocial = Object.values(socialLinks).some(link => link && link.trim() !== '');
  const showSocialInHeader = hasConnectedSocial && (socialLocation === 'header' || socialLocation === 'both');
  const showSocialInFooter = hasConnectedSocial && (socialLocation === 'footer' || socialLocation === 'both' || !socialStyle.location);

  const formatModalities = (mods) => {
    if (!mods || mods.length === 0) return null;
    const featured = mods.slice(0, 5);
    return featured
      .map(m => m.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '))
      .join(' • ');
  };

  // Colors
  const getColors = () => {
    if (website.customColors && Object.keys(website.customColors).length > 0) {
      const clrs = website.customColors;
      const bg = clrs.background || '#FFFFFF';
      return {
        primary: clrs.primary || '#5D1CE1',
        secondary: clrs.secondary || '#BB9749',
        background: bg,
        text: clrs.text || getTextColor(bg),
        secondaryText: clrs.secondaryText || getSecondaryTextColor(bg),
        accent: clrs.accent || clrs.primary || '#5D1CE1',
      };
    }
    if (website.colorScheme && PRESET_COLORS[website.colorScheme]) {
      const preset = PRESET_COLORS[website.colorScheme];
      return {
        ...preset,
        text: getTextColor(preset.background),
        secondaryText: getSecondaryTextColor(preset.background),
      };
    }
    return {
      primary: '#5D1CE1',
      secondary: '#BB9749',
      background: '#FFFFFF',
      text: '#1F2937',
      secondaryText: '#6B7280',
    };
  };

  const colors = getColors();

  const sectionStyle = draft.design.sectionStyle || 'divided';

  const getSectionStyle = (sectionIndex, isVisible = true) => {
    if (!isVisible) return {};
    const baseStyles = {
      padding: sectionStyle === 'clean'
        ? 'clamp(32px, 6vw, 64px) clamp(16px, 5vw, 40px)'
        : 'clamp(24px, 5vw, 48px) clamp(16px, 5vw, 40px)',
      backgroundColor: colors.background,
    };
    if (sectionStyle === 'clean') return baseStyles;
    if (sectionStyle === 'divided') {
      return {
        ...baseStyles,
        borderBottom: `1px solid ${hexToRgba(colors.primary, 0.15)}`,
      };
    }
    if (sectionStyle === 'alternating') {
      const isEven = sectionIndex % 2 === 0;
      return {
        ...baseStyles,
        backgroundColor: isEven ? colors.background : getAltBackground(colors.background),
      };
    }
    return baseStyles;
  };

  const getNameStyle = () => {
    const style = NAME_STYLES.find(s => s.id === website.styledNameFont);
    return style?.style || NAME_STYLES[0].style;
  };

  const getFontStyles = () => {
    const fontStyle = FONT_STYLES.find(f => f.id === website.fontStyle) || FONT_STYLES[0];
    return { heading: fontStyle.headingFont, body: fontStyle.bodyFont };
  };

  const fonts = getFontStyles();

  const getButtonStyle = (isPrimary = true, isNav = false) => {
    const buttonStyleSettings = draft.design.buttonStyle || { cornerRadius: 'rounded', size: 'medium' };
    const borderRadiusMap = { 'sharp': '0px', 'rounded': '8px', 'very-rounded': '9999px' };
    const sizeMap = {
      'compact': { padding: '6px 16px', fontSize: '13px' },
      'medium': { padding: '10px 24px', fontSize: '14px' },
      'large': { padding: '14px 32px', fontSize: '16px' },
    };
    const radius = borderRadiusMap[buttonStyleSettings.cornerRadius] || '8px';
    const sizing = sizeMap[buttonStyleSettings.size] || sizeMap.medium;
    const base = {
      fontFamily: fonts.body,
      borderRadius: radius,
      padding: sizing.padding,
      fontSize: sizing.fontSize,
      transition: 'all 0.2s ease',
      display: 'inline-block',
    };
    if (layoutStyle === 'minimal') {
      return {
        ...base,
        fontWeight: '400',
        backgroundColor: 'transparent',
        color: isPrimary ? (isNav ? 'white' : colors.primary) : colors.text,
        border: isPrimary ? `1px solid ${isNav ? 'white' : colors.primary}` : 'none',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      };
    }
    if (layoutStyle === 'warm') {
      return {
        ...base,
        fontWeight: '500',
        backgroundColor: isNav ? 'white' : colors.primary,
        color: isNav ? colors.primary : 'white',
        border: 'none',
      };
    }
    return {
      ...base,
      fontWeight: '700',
      backgroundColor: isNav ? 'white' : colors.primary,
      color: isNav ? colors.primary : 'white',
      border: 'none',
      boxShadow: isPrimary && !isNav ? `0 8px 24px -8px ${colors.primary}80` : 'none',
    };
  };

  const getHeadingStyle = (size = 'large') => {
    const base = { fontFamily: fonts.heading, color: colors.text };
    if (layoutStyle === 'minimal') {
      return {
        ...base,
        fontSize: size === 'large' ? 'clamp(1.25rem, 5vw, 2rem)' : size === 'medium' ? 'clamp(1rem, 3vw, 1.25rem)' : '1rem',
        fontWeight: '300',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      };
    }
    if (layoutStyle === 'warm') {
      return {
        ...base,
        fontSize: size === 'large' ? 'clamp(1.5rem, 6vw, 2.5rem)' : size === 'medium' ? 'clamp(1.125rem, 4vw, 1.5rem)' : '1.125rem',
        fontWeight: '600',
        letterSpacing: 'normal',
      };
    }
    return {
      ...base,
      fontSize: size === 'large' ? 'clamp(1.75rem, 7vw, 3.5rem)' : size === 'medium' ? 'clamp(1.25rem, 4.5vw, 2rem)' : '1.25rem',
      fontWeight: '800',
      letterSpacing: '-0.02em',
    };
  };

  const sectionHeadingStyle = {
    fontFamily: fonts.heading,
    fontSize: '24px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: colors.text,
    textAlign: 'center',
    marginBottom: '8px',
  };

  const sectionSubtitleStyle = {
    fontSize: '15px',
    color: '#6B7280',
    textAlign: 'center',
    marginTop: '8px',
    marginBottom: '32px',
    fontFamily: fonts.body,
  };

  // ── Hero settings ─────────────────────────────────────────────────────────────

  const heroSettings = website.heroSettings || {};
  const heroType = heroSettings.type || 'color';
  const heroHeight = heroSettings.height || 'medium';
  const textAlign = heroSettings.textAlign || 'center';
  const overlay = heroSettings.overlay || 'none';

  const heights = { short: '200px', medium: '300px', tall: '400px', full: '500px' };
  const heroHeightValue = heights[heroHeight] || '300px';

  const getHeroStyle = () => {
    switch (heroType) {
      case 'gradient':
        return {
          background: `linear-gradient(${heroSettings.gradientDirection || 'to bottom'}, ${heroSettings.gradientStartColor || colors.primary}, ${heroSettings.gradientEndColor || colors.secondary})`,
        };
      case 'image':
      case 'custom-image':
        return { backgroundColor: 'transparent' };
      default:
        return { backgroundColor: heroSettings.heroColor || colors.primary };
    }
  };

  const getHeroImageUrl = () => {
    if (heroType === 'image' && heroSettings.imageUrl) return heroSettings.imageUrl;
    if (heroType === 'custom-image' && heroSettings.customImageUrl) return heroSettings.customImageUrl;
    return null;
  };

  const heroImageUrl = getHeroImageUrl();
  const overlayClasses = { none: '', light: 'bg-black/20', medium: 'bg-black/40', dark: 'bg-black/60' };
  const overlayClass = overlayClasses[overlay] || '';
  const alignClasses = {
    left: 'items-start text-left',
    center: 'items-center text-center',
    right: 'items-end text-right',
  };
  const alignClass = alignClasses[textAlign] || 'items-center text-center';

  const heroHasImage = (heroType === 'image' && heroSettings.imageUrl) ||
    (heroType === 'custom-image' && heroSettings.customImageUrl);

  const getHeroTextColor = () => {
    if (heroHasImage) return '#FFFFFF';
    if (heroType === 'color') return getTextColor(heroSettings.heroColor || colors.primary || '#5D1CE1');
    if (heroType === 'gradient') return getTextColor(heroSettings.gradientStartColor || colors.primary || '#5D1CE1');
    return '#FFFFFF';
  };

  const heroTextColor = getHeroTextColor();
  const heroSecondaryColor = heroType === 'image' || heroType === 'custom-image'
    ? 'rgba(255,255,255,0.8)'
    : getSecondaryTextColor(
        heroType === 'color'
          ? (heroSettings.heroColor || colors.primary || '#5D1CE1')
          : (heroSettings.gradientStartColor || colors.primary || '#5D1CE1')
      );

  // ── Visible sections (for alternating backgrounds) ───────────────────────────

  const visibleSections = [
    // Default to visible when sectionVisibility[id] is undefined (backward compat
    // for practitioners who never touched the toggle).
    { id: 'about', visible: sectionVisibility?.about !== false },
    { id: 'services', visible: true },
    { id: 'testimonials', visible: draftTestimonials.length > 0 },
    { id: 'gallery', visible: showGallery && draftGallery.length > 0 },
    { id: 'events', visible: draft.content.events?.isVisible !== false && !!practitionerId },
    { id: 'faqs', visible: sectionVisibility?.faqs !== false && draftFAQs.length > 0 },
    { id: 'newsletter', visible: !!draftNewsletter?.enabled },
    { id: 'contact', visible: (
      draft.contactFooter.showEmail ||
      draft.contactFooter.showPhone ||
      draft.contactFooter.showAddress ||
      draft.contactFooter.showContactForm
    )},
  ];

  const getSectionIndex = (sectionId) => {
    const visibleOnly = visibleSections.filter(s => s.visible);
    return visibleOnly.findIndex(s => s.id === sectionId);
  };

  // ── Website content JSX ──────────────────────────────────────────────────────

  const websiteContent = (
    <div style={{
      backgroundColor: !embedded ? '#f5f5f7' : 'transparent',
    }}>
    <div style={{
      backgroundColor: colors.background,
      ...(!embedded ? {
        maxWidth: '1440px',
        margin: '0 auto',
        minHeight: '100vh',
        position: 'relative',
        boxShadow: '0 0 40px rgba(0,0,0,0.06)',
      } : {}),
    }}>
      {/* CSS Animation for section labels */}
      {embedded && (
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
        `}</style>
      )}
      {/* Responsive styles */}
      <style>{`
        /* Hamburger: flex on mobile, hidden on desktop */
        .ws-hamburger { display: flex; align-items: center; justify-content: center; }
        @media (min-width: 768px) { .ws-hamburger { display: none !important; } }

        /* Desktop nav: hidden on mobile, flex on desktop */
        .ws-nav-desktop { display: none; }
        @media (min-width: 768px) { .ws-nav-desktop { display: flex; } }

        /* Service cards */
        @media (max-width: 767px) {
          .ws-service-card { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .ws-service-card-3col { width: calc(50% - 10px) !important; min-width: 0 !important; }
        }

        /* Contact form: stack on mobile */
        @media (max-width: 599px) {
          .ws-contact-row { flex-direction: column !important; }
          .ws-contact-row > div { flex: none !important; width: 100% !important; }
        }

        /* Mobile nav animation */
        @keyframes ws-slide-down {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ws-mobile-nav { animation: ws-slide-down 0.2s ease-out; }

        ${!embedded ? 'body, html { background-color: #f5f5f7; }' : ''}
      `}</style>

      {/* Navigation */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          backgroundColor: layoutStyle === 'minimal' ? colors.background : colors.primary,
          borderBottom: layoutStyle === 'minimal' ? `1px solid ${colors.secondaryText}20` : 'none',
        }}
      >
        <div
          className="flex items-center justify-between"
          style={{
            padding: layoutStyle === 'bold' ? '20px clamp(16px, 4vw, 48px)' : layoutStyle === 'minimal' ? '12px clamp(16px, 4vw, 48px)' : '16px clamp(16px, 4vw, 48px)',
            maxWidth: '1440px',
            margin: '0 auto',
          }}
        >
          {/* Left: Logo OR styled name */}
          <div className="flex items-center">
            {website.logoOption === 'upload' && website.uploadedLogo ? (
              <img
                src={website.uploadedLogo}
                alt="Logo"
                style={{
                  height: draft.contactFooter?.logo?.logoSize === 'small' ? '32px' :
                          draft.contactFooter?.logo?.logoSize === 'large' ? '64px' : '48px',
                  width: 'auto',
                  objectFit: 'contain',
                  maxWidth: '160px',
                  mixBlendMode: draft.contactFooter?.logo?.removeBackground ? 'multiply' : 'normal',
                }}
              />
            ) : website.logoOption === 'styledText' ? (
              <span style={{ ...getNameStyle(), fontSize: 'clamp(14px, 3vw, 20px)', color: layoutStyle === 'minimal' ? colors.text : 'white' }}>
                {displayName}
              </span>
            ) : (
              <span
                style={{
                  fontFamily: fonts.heading,
                  color: layoutStyle === 'minimal' ? colors.text : 'white',
                  fontSize: layoutStyle === 'bold' ? 'clamp(1rem, 3vw, 1.5rem)' : 'clamp(0.875rem, 2.5vw, 1.125rem)',
                  fontWeight: layoutStyle === 'bold' ? '800' : layoutStyle === 'minimal' ? '400' : '600',
                  letterSpacing: layoutStyle === 'minimal' ? '0.05em' : 'normal',
                  textTransform: layoutStyle === 'minimal' ? 'uppercase' : 'none',
                }}
              >
                {displayName}
              </span>
            )}
          </div>

          {/* Right: Desktop nav links + Social (if header) */}
          <div className="ws-nav-desktop items-center gap-6" style={{ gap: '24px' }}>
            <nav
              className="flex items-center gap-4"
              style={{
                fontFamily: fonts.body,
                textTransform: layoutStyle === 'minimal' ? 'uppercase' : 'none',
                letterSpacing: layoutStyle === 'minimal' ? '0.1em' : 'normal',
                fontSize: layoutStyle === 'minimal' ? '0.75rem' : '0.875rem',
              }}
            >
              {['Home', 'About', 'Services', ...(draft.content.events?.isVisible && isPublicSite ? ['Events'] : []), 'Contact'].map((label) => {
                const sectionId = label.toLowerCase();
                const linkColor = layoutStyle === 'minimal' ? colors.secondaryText : 'rgba(255,255,255,0.8)';
                return (
                  <span
                    key={label}
                    onClick={!embedded ? () => {
                      if (sectionId === 'home') {
                        const heroSection = document.querySelector('[data-section="hero"]');
                        if (heroSection) {
                          heroSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      } else {
                        const section = document.querySelector(`[data-section="${sectionId}"]`);
                        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    } : undefined}
                    style={{
                      color: linkColor,
                      cursor: embedded ? 'default' : 'pointer',
                      transition: embedded ? 'none' : 'opacity 0.2s ease',
                    }}
                    onMouseEnter={!embedded ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
                    onMouseLeave={!embedded ? (e) => { e.currentTarget.style.opacity = layoutStyle === 'minimal' ? '1' : '0.8'; } : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </nav>

            {showSocialInHeader && (
              <>
                <div className="w-px h-5" style={{ backgroundColor: layoutStyle === 'minimal' ? colors.secondaryText + '40' : 'rgba(255,255,255,0.3)' }} />
                <SocialIcons socialLinks={socialLinks} socialStyle={socialStyle} socialVerified={socialVerified} size="md" primaryColor={layoutStyle === 'minimal' ? colors.primary : 'white'} />
              </>
            )}
          </div>

          {/* Mobile hamburger — only on public site */}
          {!embedded && (
            <button
              className="ws-hamburger"
              onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(!mobileMenuOpen); }}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                color: layoutStyle === 'minimal' ? colors.text : 'rgba(255,255,255,0.9)',
                minWidth: '44px',
                minHeight: '44px',
                borderRadius: '6px',
              }}
            >
              {mobileMenuOpen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          )}
        </div>

        {/* Mobile nav menu */}
        {!embedded && mobileMenuOpen && (
          <div
            className="md:hidden ws-mobile-nav"
            style={{
              backgroundColor: layoutStyle === 'minimal' ? colors.background : colors.primary,
              borderTop: `1px solid ${layoutStyle === 'minimal' ? colors.secondaryText + '20' : 'rgba(255,255,255,0.15)'}`,
              padding: '8px clamp(16px, 4vw, 40px) 20px',
            }}
          >
            {['About', 'Services', ...(draft.content.events?.isVisible && isPublicSite ? ['Events'] : []), 'Contact'].map((label) => (
              <div
                key={label}
                onClick={() => {
                  const section = document.querySelector(`[data-section="${label.toLowerCase()}"]`);
                  if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setMobileMenuOpen(false);
                }}
                style={{
                  padding: '14px 0',
                  borderBottom: `1px solid ${layoutStyle === 'minimal' ? colors.secondaryText + '20' : 'rgba(255,255,255,0.15)'}`,
                  fontFamily: fonts.body,
                  fontSize: '16px',
                  fontWeight: '500',
                  color: layoutStyle === 'minimal' ? colors.text : 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  textTransform: layoutStyle === 'minimal' ? 'uppercase' : 'none',
                  letterSpacing: layoutStyle === 'minimal' ? '0.08em' : 'normal',
                }}
              >
                {label}
              </div>
            ))}
            <div style={{ marginTop: '20px' }}>
              <span
                style={{
                  ...getButtonStyle(true, false),
                  display: 'block',
                  textAlign: 'center',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: layoutStyle === 'minimal' ? colors.primary : 'rgba(255,255,255,0.95)',
                  color: layoutStyle === 'minimal' ? 'white' : colors.primary,
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const section = document.querySelector('[data-section="services"]');
                  if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setMobileMenuOpen(false);
                }}
              >
                Book a Session
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Hero Section — Full Bleed (background spans full viewport width; content centered) */}
      <div
        className={`relative ${getSectionClassName('hero')}`}
        onClick={() => handleSectionClick('hero', 'background')}
        onMouseEnter={() => setHoveredSection('hero')}
        onMouseLeave={() => setHoveredSection(null)}
        data-section="hero"
        style={{
          ...(!heroImageUrl ? getHeroStyle() : {}),
          position: 'relative',
          overflow: 'hidden',
          cursor: embedded ? 'pointer' : 'default',
          borderRadius: embedded ? '4px' : '0',
          minHeight: heroHeightValue,
          maxHeight: !embedded ? '580px' : 'none',
        }}
      >
        {/* Border overlay */}
        {embedded && (hoveredSection === 'hero' || isSectionActive('hero')) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              border: '2px solid #7c3aed',
              borderRadius: embedded ? '4px' : '0',
              pointerEvents: 'none',
              zIndex: 100,
              transition: 'opacity 0.15s ease',
            }}
          />
        )}
        {renderSectionLabel('hero')}

        {/* Image layer */}
        {heroImageUrl && (
          <img
            src={heroImageUrl}
            alt="Hero background"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: (() => {
                if (heroSettings.imageRepositionOffset?.y !== undefined) {
                  return `center ${50 + heroSettings.imageRepositionOffset.y}%`;
                }
                if (heroSettings.imageVerticalPosition === 'top') return 'center 15%';
                if (heroSettings.imageVerticalPosition === 'bottom') return 'center 85%';
                return 'center 50%';
              })(),
              zIndex: 0,
            }}
          />
        )}

        {/* Overlay */}
        {(heroType === 'image' || heroType === 'custom-image') && (
          <div className={`absolute inset-0 ${overlayClass}`} style={{ zIndex: 1 }} />
        )}

        {/* Bold accent bar */}
        {layoutStyle === 'bold' && (
          <div
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '4px',
              backgroundColor: heroHasImage ? 'rgba(255,255,255,0.5)' : colors.secondary,
              zIndex: 2,
            }}
          />
        )}

        {/* Content — constrained to 800px, centered, with comfortable side padding */}
        <div className={`relative flex flex-col justify-center py-12 px-6 ${alignClass}`} style={{ minHeight: heroHeightValue, zIndex: 10, maxWidth: '800px', margin: '0 auto', width: '100%' }}>
          <h1 style={{ ...getHeadingStyle('large'), color: heroTextColor, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>{heroSettings.headline || displayName}</span>
            {verification?.identityVerified && <BadgeDisplay verification={verification} size="md" />}
          </h1>

          {(heroSettings.subheadline || profileData.tagline || modalities.length > 0) && (
            <p
              style={{
                fontFamily: fonts.body,
                color: heroSecondaryColor,
                fontSize: layoutStyle === 'bold' ? '1.25rem' : '1.125rem',
                marginBottom: '24px',
                fontWeight: layoutStyle === 'bold' ? '500' : '400',
              }}
            >
              {heroSettings.subheadline || profileData.tagline || (modalities.length > 0 ? formatModalities(modalities) : 'Your tagline will appear here')}
            </p>
          )}

          <span
            style={{
              ...getButtonStyle(true, false),
              backgroundColor: heroTextColor === '#FFFFFF' ? 'rgba(255,255,255,0.95)' : colors.primary,
              color: heroTextColor === '#FFFFFF' ? colors.primary : '#FFFFFF',
              border: layoutStyle === 'minimal' ? `2px solid ${heroTextColor === '#FFFFFF' ? 'white' : colors.primary}` : 'none',
              cursor: embedded ? 'default' : 'pointer',
            }}
            onClick={!embedded ? () => {
              const section = document.querySelector('[data-section="services"]');
              if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } : undefined}
          >
            Book a Session
          </span>
        </div>
      </div>

      {/* About Section — gated by sectionVisibility.about (default visible if undefined) */}
      {sectionVisibility?.about !== false && (
      <div
        data-section="about"
        ref={setAnimRef('about')}
        onMouseEnter={() => setHoveredSection('about')}
        onMouseLeave={() => setHoveredSection(null)}
        onClick={() => handleSectionClick('about', 'about')}
        style={{ ...getSectionStyle(getSectionIndex('about')), ...getHoverStyle('about'), ...getScrollAnimStyle('about') }}
      >
        {renderSectionLabel('about')}
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <h2 style={sectionHeadingStyle}>About</h2>

          {aboutPhotoSettings.show && profileData.photo ? (
            (aboutPhotoSettings.position === 'wrapped-left' || aboutPhotoSettings.position === 'wrapped-right') ? (
              <div style={{ overflow: 'hidden' }}>
                <img
                  src={profileData.photo}
                  alt={displayName}
                  className={aboutPhotoSettings.shape === 'circle' ? 'rounded-full' : 'rounded-lg'}
                  style={{
                    float: aboutPhotoSettings.position === 'wrapped-left' ? 'left' : 'right',
                    marginRight: aboutPhotoSettings.position === 'wrapped-left' ? '24px' : '0',
                    marginLeft: aboutPhotoSettings.position === 'wrapped-right' ? '24px' : '0',
                    marginBottom: '16px',
                    width: aboutPhotoSettings.size === 'small' ? '120px' : aboutPhotoSettings.size === 'medium' ? '160px' : '220px',
                    height: aboutPhotoSettings.size === 'small' ? '120px' : aboutPhotoSettings.size === 'medium' ? '160px' : '220px',
                    objectFit: 'cover',
                    border: aboutPhotoSettings.border === 'subtle' ? '2px solid #e5e7eb' :
                            aboutPhotoSettings.border === 'bold' ? `3px solid ${colors.primary}` : 'none',
                    boxShadow: aboutPhotoSettings.border === 'subtle' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  }}
                />
                <div
                  style={{ fontFamily: fonts.body, color: colors.secondaryText, fontSize: '1rem', lineHeight: '1.7' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(aboutContent) || '<p>Your story will appear here...</p>' }}
                />
                <div style={{ clear: 'both' }} />
              </div>
            ) : aboutPhotoSettings.position === 'above' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
                <img
                  src={profileData.photo}
                  alt={displayName}
                  className={`object-cover ${aboutPhotoSettings.shape === 'circle' ? 'rounded-full' : aboutPhotoSettings.shape === 'square' ? '' : 'rounded-lg'}`}
                  style={{
                    width: aboutPhotoSettings.size === 'small' ? '120px' : aboutPhotoSettings.size === 'medium' ? '160px' : '220px',
                    height: aboutPhotoSettings.size === 'small' ? '120px' : aboutPhotoSettings.size === 'medium' ? '160px' : '220px',
                    border: aboutPhotoSettings.border === 'subtle' ? '2px solid #e5e7eb' :
                            aboutPhotoSettings.border === 'bold' ? `3px solid ${colors.primary}` : 'none',
                    boxShadow: aboutPhotoSettings.border === 'subtle' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                    borderRadius: aboutPhotoSettings.shape === 'square' ? '0' : undefined,
                  }}
                />
                <div
                  style={{ fontFamily: fonts.body, color: colors.secondaryText, fontSize: '1rem', lineHeight: '1.7', textAlign: 'center' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(aboutContent) || '<p>Your story will appear here...</p>' }}
                />
              </div>
            ) : (
              <div className={`flex gap-6 items-start ${aboutPhotoSettings.position === 'right' ? 'flex-row-reverse' : ''}`}>
                <div className="flex-shrink-0">
                  <img
                    src={profileData.photo}
                    alt={displayName}
                    className={`object-cover ${aboutPhotoSettings.shape === 'circle' ? 'rounded-full' : aboutPhotoSettings.shape === 'square' ? '' : 'rounded-lg'}`}
                    style={{
                      width: aboutPhotoSettings.size === 'small' ? '120px' : aboutPhotoSettings.size === 'medium' ? '160px' : '220px',
                      height: aboutPhotoSettings.size === 'small' ? '120px' : aboutPhotoSettings.size === 'medium' ? '160px' : '220px',
                      border: aboutPhotoSettings.border === 'subtle' ? '2px solid #e5e7eb' :
                              aboutPhotoSettings.border === 'bold' ? `3px solid ${colors.primary}` : 'none',
                      boxShadow: aboutPhotoSettings.border === 'subtle' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                      borderRadius: aboutPhotoSettings.shape === 'square' ? '0' : undefined,
                    }}
                  />
                </div>
                <div
                  style={{ fontFamily: fonts.body, color: colors.secondaryText, fontSize: '1rem', lineHeight: '1.7', textAlign: 'left' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(aboutContent) || '<p>Your story will appear here...</p>' }}
                />
              </div>
            )
          ) : (
            <div
              style={{ fontFamily: fonts.body, color: colors.secondaryText, fontSize: '1rem', lineHeight: '1.7', textAlign: 'center' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(aboutContent) || '<p>Your story will appear here. Share what drew you to this work and how you help your clients...</p>' }}
            />
          )}
        </div>
      </div>
      )}

      {/* Services Section */}
      <div
        className={getSectionClassName('services')}
        onClick={() => handleSectionClick('services', 'services')}
        onMouseEnter={() => setHoveredSection('services')}
        onMouseLeave={() => setHoveredSection(null)}
        data-section="services"
        ref={setAnimRef('services')}
        style={{ ...getSectionStyle(getSectionIndex('services')), ...getHoverStyle('services'), ...getScrollAnimStyle('services') }}
      >
        {renderSectionLabel('services')}
        <div>
          <h2 style={sectionHeadingStyle}>Services</h2>
          <p style={sectionSubtitleStyle}>Explore my offerings and find what resonates with you</p>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            justifyContent: 'center',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 24px',
          }}
        >
          {services && services.filter(s => s.active).length > 0 ? (
            services.filter(s => s.active).map((service) => {
              const activeServices = services.filter(s => s.active);
              const serviceCount = activeServices.length;
              let cardStyle = {};
              if (serviceCount === 1) {
                cardStyle = { width: '100%', maxWidth: '400px' };
              } else if (serviceCount === 2) {
                cardStyle = { width: 'calc(50% - 10px)', maxWidth: '450px' };
              } else {
                cardStyle = { width: 'calc(33.333% - 14px)', minWidth: '280px', maxWidth: '450px' };
              }
              return (
                <div key={service.id} style={cardStyle} className={`ws-service-card${serviceCount >= 3 ? ' ws-service-card-3col' : ''}`}>
                  <ServiceCard
                    service={service}
                    primaryColor={colors.primary}
                    textColor={colors.text}
                    headingFont={fonts.heading}
                    bodyFont={fonts.body}
                    onClick={(serviceId) => setViewingServiceDetail(serviceId)}
                  />
                </div>
              );
            })
          ) : (
            <div style={{ width: '100%', textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ fontSize: '14px', color: '#9CA3AF', fontFamily: fonts.body }}>
                No services available yet
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Testimonials Section */}
      {draftTestimonials.length > 0 && (
        <section
          className={getSectionClassName('testimonials')}
          onClick={() => handleSectionClick('testimonials', 'testimonials')}
          onMouseEnter={() => setHoveredSection('testimonials')}
          onMouseLeave={() => setHoveredSection(null)}
          data-section="testimonials"
          ref={setAnimRef('testimonials')}
          style={{ ...getSectionStyle(getSectionIndex('testimonials'), draftTestimonials.length > 0), ...getHoverStyle('testimonials'), ...getScrollAnimStyle('testimonials') }}
        >
          {renderSectionLabel('testimonials')}
          <div style={{ maxWidth: '960px', margin: '0 auto', width: '100%' }}>
            <h2 style={{ ...sectionHeadingStyle, marginBottom: '48px' }}>What Clients Say</h2>
            <div
              className="grid gap-6"
              style={{
                gridTemplateColumns: draftTestimonials.length === 1 ? '1fr' :
                  draftTestimonials.length === 2 ? 'repeat(2, 1fr)' :
                  'repeat(auto-fit, minmax(280px, 1fr))',
              }}
            >
              {draftTestimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  primaryColor={colors.primary}
                  accentColor={colors.secondary}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Published Reviews Section (public site only — from Firestore) */}
      {isPublicSite && publishedReviews.length > 0 && (
        <section
          data-section="reviews"
          ref={setAnimRef('reviews')}
          style={{ ...getSectionStyle(getSectionIndex('reviews'), publishedReviews.length > 0), ...getScrollAnimStyle('reviews') }}
        >
          <div style={{ maxWidth: '960px', margin: '0 auto', width: '100%' }}>
            <h2 style={{ ...sectionHeadingStyle, marginBottom: '12px' }}>Client Reviews</h2>

            {/* Average rating summary */}
            {(() => {
              const avg = publishedReviews.reduce((s, r) => s + (r.rating || 0), 0) / publishedReviews.length;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '40px' }}>
                  {[1,2,3,4,5].map(s => (
                    <svg key={s} width="20" height="20" viewBox="0 0 24 24" fill={s <= Math.round(avg) ? '#F59E0B' : 'none'} stroke="#F59E0B" strokeWidth="1.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  ))}
                  <span style={{ fontSize: '15px', fontWeight: '600', color: colors.text }}>{avg.toFixed(1)}</span>
                  <span style={{ fontSize: '14px', color: colors.secondaryText }}>({publishedReviews.length} review{publishedReviews.length !== 1 ? 's' : ''})</span>
                </div>
              );
            })()}

            <div
              className="grid gap-5"
              style={{
                gridTemplateColumns: publishedReviews.length === 1 ? '1fr' :
                  publishedReviews.length === 2 ? 'repeat(2, 1fr)' :
                  'repeat(auto-fit, minmax(280px, 1fr))',
              }}
            >
              {publishedReviews.map(review => (
                <div
                  key={review.id}
                  style={{
                    background: getAltBackground(colors.background),
                    borderRadius: layout.cardRadius,
                    padding: '24px',
                    border: layout.cardBorder ? `1px solid ${hexToRgba(colors.text, 0.12)}` : 'none',
                    boxShadow: layout.cardShadow,
                  }}
                >
                  {/* Stars */}
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '12px' }}>
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill={s <= (review.rating || 0) ? '#F59E0B' : 'none'} stroke="#F59E0B" strokeWidth="1.5">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    ))}
                  </div>

                  {/* Body */}
                  <p style={{ fontSize: '15px', color: colors.text, lineHeight: '1.7', marginBottom: '16px' }}>
                    "{review.body}"
                  </p>

                  {/* Author */}
                  <p style={{ fontSize: '13px', fontWeight: '600', color: colors.secondaryText }}>
                    — {review.isAnonymous ? 'Anonymous Client' : (review.displayName || 'Client')}
                  </p>

                  {/* Practitioner response */}
                  {review.practitionerResponse && (
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: `1px solid ${hexToRgba(colors.text, 0.1)}`,
                    }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: colors.primary, marginBottom: '4px' }}>
                        Response from {practitionerName}
                      </p>
                      <p style={{ fontSize: '13px', color: colors.secondaryText, lineHeight: '1.6' }}>
                        {review.practitionerResponse}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Google Reviews Section (public site only — read-only, clearly attributed) */}
      {isPublicSite && googleReviews.length > 0 && (
        <section
          data-section="google-reviews"
          ref={setAnimRef('google-reviews')}
          style={{ ...getSectionStyle(getSectionIndex('google-reviews'), googleReviews.length > 0), ...getScrollAnimStyle('google-reviews') }}
        >
          <div style={{ maxWidth: '960px', margin: '0 auto', width: '100%' }}>
            {/* Section heading with Google attribution */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', marginBottom: '12px' }}>
              {/* Google "G" logo inline SVG — required by Google ToS */}
              <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <h2 style={{ ...sectionHeadingStyle, marginBottom: 0 }}>Google Reviews</h2>
            </div>
            <p style={{ textAlign: 'center', fontSize: '13px', color: colors.secondaryText, marginBottom: '40px' }}>
              Reviews from Google
            </p>

            <div
              className="grid gap-5"
              style={{
                gridTemplateColumns: googleReviews.length === 1 ? '1fr' :
                  googleReviews.length === 2 ? 'repeat(2, 1fr)' :
                  'repeat(auto-fit, minmax(280px, 1fr))',
              }}
            >
              {googleReviews.map(review => (
                <div
                  key={review.id}
                  style={{
                    background: getAltBackground(colors.background),
                    borderRadius: layout.cardRadius,
                    padding: '24px',
                    border: layout.cardBorder ? `1px solid ${hexToRgba(colors.text, 0.12)}` : 'none',
                    boxShadow: layout.cardShadow,
                  }}
                >
                  {/* Reviewer info row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    {review.profilePhotoUrl ? (
                      <img
                        src={review.profilePhotoUrl}
                        alt={review.authorName || 'Reviewer'}
                        style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: hexToRgba(colors.primary, 0.12),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, fontSize: '15px', fontWeight: '600', color: colors.primary,
                      }}>
                        {(review.authorName || 'G')[0]}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {review.authorName || 'Google User'}
                      </p>
                      <p style={{ fontSize: '12px', color: colors.secondaryText, margin: 0 }}>
                        {review.relativeTimeDescription || ''}
                      </p>
                    </div>
                    {/* Google G logo (small, attribution) */}
                    <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, opacity: 0.7 }}>
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>

                  {/* Stars */}
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '10px' }}>
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} width="15" height="15" viewBox="0 0 24 24" fill={s <= (review.rating || 0) ? '#F59E0B' : 'none'} stroke="#F59E0B" strokeWidth="1.5">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    ))}
                  </div>

                  {/* Review text */}
                  {review.text ? (
                    <p style={{ fontSize: '14px', color: colors.text, lineHeight: '1.7', margin: 0 }}>
                      {review.text}
                    </p>
                  ) : (
                    <p style={{ fontSize: '14px', color: colors.secondaryText, fontStyle: 'italic', margin: 0 }}>
                      No written review
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {showGallery && draftGallery.length > 0 && (
        <section
          className={getSectionClassName('gallery')}
          onClick={() => handleSectionClick('gallery', 'gallery')}
          onMouseEnter={(e) => { e.stopPropagation(); setHoveredSection('gallery'); }}
          onMouseLeave={() => setHoveredSection(null)}
          data-section="gallery"
          ref={setAnimRef('gallery')}
          style={{ ...getSectionStyle(getSectionIndex('gallery'), draftGallery.length > 0), ...getHoverStyle('gallery'), ...getScrollAnimStyle('gallery') }}
        >
          {renderSectionLabel('gallery')}
          <h2 style={{ ...sectionHeadingStyle, marginBottom: '48px' }}>Gallery</h2>
          {galleryLayout === 'grid' && <GalleryGrid images={draftGallery} onImageClick={handleImageClick} />}
          {galleryLayout === 'masonry' && <GalleryMasonry images={draftGallery} onImageClick={handleImageClick} />}
        </section>
      )}

      {/* Events Section — renders when toggled on and practitioner is known */}
      {draft.content.events?.isVisible !== false && practitionerId && (
        <div
          data-section="events"
          ref={setAnimRef('events')}
          onMouseEnter={() => setHoveredSection('events')}
          onMouseLeave={() => setHoveredSection(null)}
          style={{ ...getSectionStyle(getSectionIndex('events')), ...getHoverStyle('events'), ...getScrollAnimStyle('events') }}
        >
          {renderSectionLabel('events')}
          <EventsSection
            practitionerId={practitionerId}
            practitionerSlug={practitionerSlug}
            settings={draft.content.events}
            headingStyle={sectionHeadingStyle}
          />
        </div>
      )}

      {/* FAQ Section — gated by sectionVisibility.faqs (default visible if undefined) AND content presence */}
      {sectionVisibility?.faqs !== false && draftFAQs.length > 0 && (
        <div
          className={getSectionClassName('faqs')}
          onClick={() => handleSectionClick('faqs', 'faqs')}
          onMouseEnter={() => setHoveredSection('faqs')}
          onMouseLeave={() => setHoveredSection(null)}
          data-section="faqs"
          ref={setAnimRef('faqs')}
          style={{ ...getSectionStyle(getSectionIndex('faqs'), draftFAQs.length > 0), ...getHoverStyle('faqs'), ...getScrollAnimStyle('faqs') }}
        >
          {renderSectionLabel('faqs')}
          <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            <h2 style={{ ...sectionHeadingStyle, marginBottom: '32px' }}>FAQs</h2>
            <div className="space-y-3">
              {draftFAQs.map((faq) => (
                <FAQAccordionItem key={faq.id} faq={faq} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Signup Section */}
      {draftNewsletter?.enabled && (
        <div
          onClick={() => handleSectionClick('newsletter', 'newsletter')}
          onMouseEnter={() => setHoveredSection('newsletter')}
          onMouseLeave={() => setHoveredSection(null)}
          data-section="newsletter"
          ref={setAnimRef('newsletter')}
          style={{ position: 'relative', cursor: embedded ? 'pointer' : 'default', ...getScrollAnimStyle('newsletter') }}
        >
          {embedded && (hoveredSection === 'newsletter' || isSectionActive('newsletter')) && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                border: '2px solid #7c3aed',
                borderRadius: '4px',
                pointerEvents: 'none',
                zIndex: 100,
                transition: 'opacity 0.15s ease',
              }}
            />
          )}
          {renderSectionLabel('newsletter')}
          <NewsletterSignup
            config={draftNewsletter}
            colors={colors}
            fonts={fonts}
            buttonStyle={draft.design.buttonStyle}
            headingStyle={sectionHeadingStyle}
            practitionerUid={practitionerId}
          />
        </div>
      )}

      {/* Contact Section */}
      {(draft.contactFooter.showEmail || draft.contactFooter.showPhone ||
        draft.contactFooter.showAddress || draft.contactFooter.showContactForm) && (
        <div
          style={{
            ...getSectionStyle(getSectionIndex('contact'), (
              draft.contactFooter.showEmail ||
              draft.contactFooter.showPhone ||
              draft.contactFooter.showAddress ||
              draft.contactFooter.showContactForm
            )),
            ...getHoverStyle('contact'),
            ...getScrollAnimStyle('contact'),
            textAlign: 'center',
          }}
          className={getSectionClassName('contact')}
          onClick={() => handleSectionClick('contact', 'contact')}
          onMouseEnter={() => setHoveredSection('contact')}
          onMouseLeave={() => setHoveredSection(null)}
          data-section="contact"
          ref={setAnimRef('contact')}
        >
          {renderSectionLabel('contact')}
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <h2 style={{ ...sectionHeadingStyle, marginBottom: '32px' }}>
              {draft.contactFooter.sectionTitle || 'Get in Touch'}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', marginBottom: draft.contactFooter.showContactForm ? '32px' : '0' }}>
              {draft.contactFooter.showEmail && !draft.contactFooter.showContactForm && draft.contactFooter.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Mail size={20} style={{ color: colors.primary, flexShrink: 0 }} />
                  <a
                    href={`mailto:${draft.contactFooter.email}`}
                    style={{ fontFamily: fonts.body, fontSize: '1rem', color: colors.primary, textDecoration: 'none', fontWeight: 500, transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {draft.contactFooter.email}
                  </a>
                </div>
              )}

              {draft.contactFooter.showPhone && !draft.contactFooter.showContactForm && draft.contactFooter.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Phone size={20} style={{ color: colors.primary, flexShrink: 0 }} />
                  <a
                    href={`tel:${draft.contactFooter.phone}`}
                    style={{ fontFamily: fonts.body, fontSize: '1rem', color: colors.primary, textDecoration: 'none', fontWeight: 500, transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {draft.contactFooter.phone}
                  </a>
                </div>
              )}

              {draft.contactFooter.showAddress && draft.contactFooter.address &&
               (draft.contactFooter.address.street || draft.contactFooter.address.city) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <MapPin size={20} style={{ color: colors.primary, flexShrink: 0 }} />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([
                      draft.contactFooter.address.street,
                      draft.contactFooter.address.city,
                      draft.contactFooter.address.state,
                      draft.contactFooter.address.zip,
                    ].filter(Boolean).join(', '))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontFamily: fonts.body, fontSize: '1rem', color: colors.primary, textDecoration: 'none', fontWeight: 500, transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {[
                      draft.contactFooter.address.street,
                      [draft.contactFooter.address.city, draft.contactFooter.address.state].filter(Boolean).join(', '),
                      draft.contactFooter.address.zip,
                    ].filter(Boolean).join(', ')}
                  </a>
                </div>
              )}
            </div>

            {/* Contact Form */}
            {draft.contactFooter.showContactForm && (
              <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'left' }}>
                <form style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div className="ws-contact-row" style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div>
                        <label htmlFor="contact-name" style={{ display: 'block', fontFamily: fonts.body, fontSize: '12px', fontWeight: 500, color: colors.text, marginBottom: '4px' }}>Name</label>
                        <input type="text" id="contact-name" placeholder="Your name" disabled={!isPublicSite}
                          style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.secondaryText}40`, borderRadius: layout.cardRadius, fontSize: '14px', fontFamily: fonts.body, color: colors.text, backgroundColor: colors.background, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" style={{ display: 'block', fontFamily: fonts.body, fontSize: '12px', fontWeight: 500, color: colors.text, marginBottom: '4px' }}>Email</label>
                        <input type="email" id="contact-email" placeholder="your@email.com" disabled={!isPublicSite}
                          style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.secondaryText}40`, borderRadius: layout.cardRadius, fontSize: '14px', fontFamily: fonts.body, color: colors.text, backgroundColor: colors.background, boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ flex: '1' }}>
                      <label htmlFor="contact-message" style={{ display: 'block', fontFamily: fonts.body, fontSize: '12px', fontWeight: 500, color: colors.text, marginBottom: '4px' }}>Message</label>
                      <textarea id="contact-message" placeholder="How can I help?" disabled={!isPublicSite}
                        style={{ width: '100%', height: 'calc(100% - 20px)', padding: '8px 10px', border: `1px solid ${colors.secondaryText}40`, borderRadius: layout.cardRadius, fontSize: '14px', fontFamily: fonts.body, color: colors.text, backgroundColor: colors.background, boxSizing: 'border-box', resize: 'vertical', minHeight: '90px' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: fonts.body }}>Reply to me via:</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer', fontFamily: fonts.body }}>
                      <input type="radio" name="replyMethod" value="email" defaultChecked disabled={!isPublicSite} style={{ accentColor: colors.primary }} />
                      Email
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer', fontFamily: fonts.body }}>
                      <input type="radio" name="replyMethod" value="phone" disabled={!isPublicSite} />
                      Phone
                    </label>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                    <button type="submit" disabled={!isPublicSite}
                      style={{ ...getButtonStyle(true, false), backgroundColor: colors.primary, color: '#FFFFFF', border: 'none', cursor: 'pointer', padding: '10px 32px' }}
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer
        className={`border-t py-12 ${getSectionClassName('footer')}`}
        onClick={() => handleSectionClick('footer', 'footer')}
        onMouseEnter={() => setHoveredSection('footer')}
        onMouseLeave={() => setHoveredSection(null)}
        data-section="footer"
        style={{
          backgroundColor: getFooterBackground(colors.background),
          borderColor: 'rgba(255, 255, 255, 0.1)',
          fontFamily: fonts.body,
          ...getHoverStyle('footer'),
        }}
      >
        {renderSectionLabel('footer')}
        {(() => {
          const footerConfig = draft.contactFooter?.footerConfig || {};
          const footerLayout = footerConfig.layout || 'simple';
          const footerName = footerConfig.copyrightName || displayName || 'Your Name';
          const currentYear = new Date().getFullYear();
          const policyLinks = [];
          if (effectivePrivacyPolicy?.content) policyLinks.push({ label: 'Privacy Policy', href: '#privacy' });
          if (effectiveTermsOfService?.content) policyLinks.push({ label: 'Terms of Service', href: '#terms' });
          if (effectiveCancellationPolicy) policyLinks.push({ label: 'Cancellation Policy', href: '#cancellation' });

          if (footerLayout === 'detailed') {
            return (
              <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', padding: '0 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', marginBottom: '32px' }}>
                  <div>
                    <div style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: 600, color: '#F3F4F6', marginBottom: '8px' }}>{displayName}</div>
                    {modalities.length > 0 && (
                      <div style={{ fontFamily: fonts.body, fontSize: '14px', color: '#D1D5DB' }}>
                        {modalities.slice(0, 3).join(' • ')}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontFamily: fonts.heading, fontSize: '14px', fontWeight: 600, color: '#F3F4F6', marginBottom: '12px' }}>Quick Links</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', fontFamily: fonts.body }}>
                      {['Home', 'About', 'Services', 'Contact'].map(label => (
                        <a key={label} href={`#${label.toLowerCase()}`}
                          style={{ color: '#D1D5DB', textDecoration: 'none', transition: 'color 0.2s' }}
                          onMouseEnter={(e) => e.target.style.color = '#FFFFFF'}
                          onMouseLeave={(e) => e.target.style.color = '#D1D5DB'}
                        >{label}</a>
                      ))}
                    </div>
                  </div>
                  {showSocialInFooter && (
                    <div>
                      <div style={{ fontFamily: fonts.heading, fontSize: '14px', fontWeight: 600, color: '#F3F4F6', marginBottom: '12px' }}>Connect</div>
                      <SocialIcons socialLinks={socialLinks} socialStyle={socialStyle} socialVerified={socialVerified} size="md" primaryColor="#D1D5DB" />
                    </div>
                  )}
                </div>
                {/* Bottom row - policy links + copyright + powered by */}
                <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255, 255, 255, 0.15)', textAlign: 'center' }}>
                  {policyLinks.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '12px', fontFamily: fonts.body, fontSize: '13px', flexWrap: 'wrap' }}>
                      {policyLinks.map((link, index) => (
                        <React.Fragment key={link.label}>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPoliciesPage(true);
                              const targetMap = { '#privacy': 'privacy-policy', '#terms': 'terms-of-service', '#cancellation': 'cancellation-policy' };
                              setPoliciesScrollTarget(targetMap[link.href]);
                            }}
                            style={{ color: '#D1D5DB', textDecoration: 'none', transition: 'color 0.2s', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.target.style.color = '#FFFFFF'}
                            onMouseLeave={(e) => e.target.style.color = '#D1D5DB'}
                          >
                            {link.label}
                          </span>
                          {index < policyLinks.length - 1 && <span style={{ color: '#D1D5DB', opacity: 0.4 }}>•</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontFamily: fonts.body, fontSize: '13px', color: '#D1D5DB' }}>
                    <span>© {currentYear} {footerName}. All rights reserved.</span>
                    <>
                      <span>•</span>
                      <a href="https://www.divineframeworks.com" target="_blank" rel="noopener noreferrer"
                        style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '11px' }}
                      >Powered by Divine Frameworks</a>
                    </>
                  </div>
                </div>
              </div>
            );
          }

          // Simple layout
          return (
            <div style={{ maxWidth: '760px', margin: '0 auto', width: '100%', padding: '0 24px', textAlign: 'center' }}>
              {showSocialInFooter && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                  <SocialIcons socialLinks={socialLinks} socialStyle={socialStyle} socialVerified={socialVerified} size="md" primaryColor="#D1D5DB" />
                </div>
              )}
              {policyLinks.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '20px', fontFamily: fonts.body, fontSize: '13px', flexWrap: 'wrap' }}>
                  {policyLinks.map((link, index) => (
                    <React.Fragment key={link.label}>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPoliciesPage(true);
                          const targetMap = { '#privacy': 'privacy-policy', '#terms': 'terms-of-service', '#cancellation': 'cancellation-policy' };
                          setPoliciesScrollTarget(targetMap[link.href]);
                        }}
                        style={{ color: '#D1D5DB', textDecoration: 'none', transition: 'color 0.2s', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.target.style.color = '#FFFFFF'}
                        onMouseLeave={(e) => e.target.style.color = '#D1D5DB'}
                      >
                        {link.label}
                      </span>
                      {index < policyLinks.length - 1 && <span style={{ color: '#D1D5DB', opacity: 0.4 }}>•</span>}
                    </React.Fragment>
                  ))}
                </div>
              )}
              <p style={{ fontFamily: fonts.body, fontSize: '13px', color: '#D1D5DB', marginBottom: '8px' }}>
                © {currentYear} {footerName}. All rights reserved.
              </p>
              <a
                href="https://www.divineframeworks.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: fonts.body, fontSize: '11px', color: '#9CA3AF', textDecoration: 'none', display: 'block', marginTop: '8px', transition: 'color 0.2s' }}
                onMouseEnter={(e) => e.target.style.color = '#D1D5DB'}
                onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
              >
                Powered by Divine Frameworks
              </a>
            </div>
          );
        })()}
      </footer>

    </div>
    </div>
  );

  // ── Sub-page views (replace main view when active) ──────────────────────────

  // Service Detail
  if (viewingServiceDetail) {
    const service = services.find(s => s.id === viewingServiceDetail);
    if (!service) {
      setViewingServiceDetail(null);
      return null;
    }

    const logoElement = website.logoOption === 'upload' && website.uploadedLogo ? (
      <img
        src={website.uploadedLogo}
        alt={displayName || 'Logo'}
        style={{
          height: draft.contactFooter?.logo?.logoSize === 'small' ? '32px' :
                  draft.contactFooter?.logo?.logoSize === 'large' ? '64px' : '48px',
          width: 'auto',
          objectFit: 'contain',
          mixBlendMode: draft.contactFooter?.logo?.removeBackground ? 'multiply' : 'normal',
        }}
      />
    ) : website.logoOption === 'styled-name' ? (
      <span style={getNameStyle()}>{displayName || 'Your Name'}</span>
    ) : (
      <span
        style={{
          fontSize: layoutStyle === 'bold' ? '1.75rem' : layoutStyle === 'minimal' ? '1.125rem' : '1.5rem',
          fontWeight: layoutStyle === 'bold' ? '800' : '700',
          color: layoutStyle === 'minimal' ? colors.primary : 'white',
          letterSpacing: layoutStyle === 'minimal' ? '0.05em' : 'normal',
          textTransform: layoutStyle === 'minimal' ? 'uppercase' : 'none',
        }}
      >
        {displayName || 'Your Name'}
      </span>
    );

    return (
      <ServiceDetailPage
        service={service}
        allServices={services}
        designSettings={{
          colors,
          fonts,
          buttonStyle: draft.design.buttonStyle || { cornerRadius: 'rounded', size: 'medium' },
        }}
        practitionerName={displayName}
        logo={logoElement}
        onBack={() => setViewingServiceDetail(null)}
        onBookService={() => {
          setBookingService(service);
          setShowBookingPage(true);
          setViewingServiceDetail(null);
        }}
        isPreview={!isPublicSite}
      />
    );
  }

  // Policies Page
  if (showPoliciesPage) {
    return (
      <PoliciesPage
        privacyPolicy={effectivePrivacyPolicy}
        termsOfService={effectiveTermsOfService}
        cancellationPolicy={effectiveCancellationPolicy}
        onBack={() => {
          setShowPoliciesPage(false);
          setPoliciesScrollTarget(null);
        }}
        colors={colors}
        fonts={fonts}
        embedded={embedded}
        scrollTarget={policiesScrollTarget}
      />
    );
  }

  // Booking Page
  if (showBookingPage && bookingService) {
    return (
      <BookingPage
        service={bookingService}
        onBack={() => {
          setShowBookingPage(false);
          setBookingService(null);
          setViewingServiceDetail(bookingService.id);
        }}
        colors={colors}
        fonts={fonts}
        buttonStyle={draft.design?.buttonStyle}
        availability={availability}
        existingBookings={[]}
        practitionerName={displayName || draft.profile?.displayName || 'Your Practitioner'}
        practitionerId={practitionerId}
        embedded={embedded}
      />
    );
  }

  // ── Main return ──────────────────────────────────────────────────────────────

  return (
    <>
      {websiteContent}
      {lightboxOpen && (
        <Lightbox
          images={draftGallery}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}
    </>
  );
};

export default WebsiteRenderer;
